/* eslint-disable camelcase */
/* eslint-disable no-unused-vars */
/** @module Auth0IdentityProvider */
/* eslint-disable import/extensions */
import Auth0Constants from './auth0-constants.mjs';
import Auth0UtilsBrowser from './auth0-utils-browser.mjs';
import Auth0CookieHelper from './auth0-cookie-helper.mjs';
import logger from './logging/auth0-logger.mjs';

/**
 * @constructor
 * @description A class representing a single Auth0 Identity Provider (e.g. google, shopify, etc)
 * @param {object} idpData Initialization data for the IdP
 * @param {string} idpData.tenantOrDomain The shipstation.com signin domain or the tenant (e.g. signin.shipstation.com, signin-stage.shipstation.com, shipstation-stage, etc)
 * @param {string} idpData.id The IdP's Auth0-based ID (e.g. 'google-oauth2')
 * @param {string} idpData.name The IdP's display name (e.g. 'Google')
 * @param {string} idpData.logoUrl The IdP's icon URL. Ideally an SVG. Will be displayed on the IdP's signin button and also during the account linking process
 * @param {boolean} [idpData.loginWithPopup=false] True if popup window when signing in to the IdP (not all IdPs support popup windows)
 * @param {boolean} [idpData.isCustomAuth0Provider=false] True if the IDP was built using Auth0's "custom social connection" option
 * @param {string} [idpData.appLoginHandlerUrl] Required if this object is instantiated in a browser and you intend to call upon the signinWithRedirectHandler() function, otherwise optional
 */
class Auth0IdentityProvider {
  constructor(idpData) {
    this._id = idpData.id;
    this._name = idpData.name;
    this._logoUrl = idpData.logoUrl;
    this._loginWithPopup = idpData.loginWithPopup ?? false;
    this._isCustomAuth0Provider = idpData.isCustomAuth0Provider ?? false;
    this._userIdPrefix = idpData.isCustomAuth0Provider ? Auth0IdentityProvider.customIdpPrefix : '';
    this._appLoginHandlerUrl = idpData.appLoginHandlerUrl;
    this._alwaysAvailable = idpData.alwaysAvailable ?? false;
  }

  static get customIdpPrefix() {
    return 'oauth2|';
  }

  static get _customIdpPrefixRegex() {
    return 'oauth2\\|';
  }

  get id() {
    return this._id;
  }

  get name() {
    return this._name;
  }

  get logoUrl() {
    return this._logoUrl;
  }

  get loginWithPopup() {
    return this._loginWithPopup;
  }

  get isCustomAuth0Provider() {
    return this._isCustomAuth0Provider;
  }

  get alwaysAvailable() {
    return this._alwaysAvailable;
  }

  get userIdPrefix() {
    return this._userIdPrefix;
  }

  get userIdRegex() {
    return Auth0IdentityProvider.getUserIdRegex(this.id);
  }

  doesUserIdBelongToMe = (userId) => {
    return this.userIdRegex.test(userId);
  };

  normalizeUserId = (userId) => {
    // normalized userId is "[ [_userIdPrefix]| ][_id]|[userId]
    // this function assumes that userId is already normalized (as above) or just the ID of the user without prefix or ID
    const matches = this.userIdRegex.test(userId);

    if (matches && this._isCustomAuth0Provider && !userId.startsWith(Auth0IdentityProvider.customIdpPrefix)) {
      return `${Auth0IdentityProvider.customIdpPrefix}${userId}`;
    }
    return matches
      ? userId
      : `${this.userIdPrefix}${this.id}|${userId}`;
  };

  denormalizeUserId = (userId) => {
    const m = this.userIdRegex.exec(userId);
    return !m || !m.length
      ? userId
      : m[m.length - 1];
  };

  startIdpLoginHandler = async (appRedirectUrl, additionalState = null) => {
    console.log(`Auth0IdentityProvider.startIdpLoginHandler for ${this.id} starts...`);
    // both result in a client redirect action...
    if (this.loginWithPopup) {
      this.signinWithPopupHandler(appRedirectUrl || this._appLoginHandlerUrl);
    } else {
      this.signinWithRedirectHandler(appRedirectUrl || this._appLoginHandlerUrl, additionalState);
    }
  };

  startIdpOnboardingLoginHandler = async (options) => {
    console.log(`Auth0IdentityProvider.startIdpOnboardingForNewUser => ${JSON.stringify(options)}`);

    options.source = Auth0Constants.USER_ONBOARDING_CHANNEL_SSO;

    const newUserRequest = {};
    newUserRequest[Auth0Constants.AUTH0_ONBOARD_REQUEST_DATA_KEY] = options;
    await this.startIdpLoginHandler(options.appRedirectUrl, newUserRequest);
  };

  _setLoginWithRedirectContextForThisIdp = () => {
    Auth0UtilsBrowser.setOption({
      cookieName: Auth0Constants.AUTH0_IDP_REDIRECT_LOGIN,
      value: JSON.stringify({
        idpId: this.id,
        appLoginHandlerUrl: this._appLoginHandlerUrl,
      }),
      expirySec: Auth0Constants.GLOBAL_FLOW_TIMEOUT_SEC,
    });
  };

  _setIdpContextForThisIdp = () => {
    Auth0UtilsBrowser.setOption({
      cookieName: Auth0Constants.AUTH0_IDP_CONTEXT_KEY,
      value: JSON.stringify({
        idpId: this.id,
      }),
      expirySec: Auth0Constants.GLOBAL_FLOW_TIMEOUT_SEC,
    });
  };

  // function override candidate, no arrow functions here...
  beforeAuthRedirect(loginWithRedirectOptions, auth0Client) {
    // no default actions, override if IDP requires special actions
  }

  // function override candidate, no arrow functions here...
  afterAuthRedirect(userLoginData, auth0Client) {

  }

  // function override candidate, no arrow functions here...
  signinWithRedirectHandler(appRedirectUrl, additionalState = null) {
    // default behavior just redirects to login-helper after having written the IDP signin instructions cookie.
    // override for custom behavior when user clicks the signin button for an IDP from the login page.
    console.log(`Auth0IdentityProvider._signinWithRedirectHandler for ${this.id} starts (using redirect "${appRedirectUrl}")...`);
    try {
      this._setLoginWithRedirectContextForThisIdp();
      this._setIdpContextForThisIdp();

      const url = new URL(appRedirectUrl);
      if (additionalState) {
        console.log(`Auth0IdentityProvider._signinWithRedirectHandler: Detected additional state, adding => ${JSON.stringify(additionalState)}`);
        url.searchParams.set(Auth0Constants.AUTH0_ADDITIONAL_APP_STATE, JSON.stringify(additionalState));
      }

      window.location.href = url.toString();
    } catch (e) {
      logger.error(`Auth0IdentityProvider signinClickHandler error => ${e}`);
      throw new Error('error-unable-to-complete-idp-signin');
    }
  }

  // function override candidate, no arrow functions here...
  async signinWithPopupHandler(appRedirectUrl) {
    console.log(`Auth0IdentityProvider._signinWithPopupHandler for ${this.id} starts...`);
    try {
      const a0Options = {
        domain: this.params.domain,
        clientId: this.params.clientID,
        cacheLocation: 'localstorage',
        authorizationParams: {
          connection: this.id,
        },
      };

      console.log(`Auth0IdentityProvider._signinWithPopupHandler using a0 client with ${JSON.stringify(a0Options)}`);
      this._setIdpContextForThisIdp();
      const auth0IdPClient = await window.auth0SpaJs.createAuth0Client(a0Options);
      await auth0IdPClient.loginWithPopup({
        authorizationParams: {
          max_age: 0,
          scope: 'openid',
        },
      }, {
        popup: Auth0UtilsBrowser.openPopup(),
        timeoutInSeconds: Auth0Constants.GLOBAL_FLOW_TIMEOUT_SEC,
      });
      const isAuthenticated = await auth0IdPClient.isAuthenticated();
      console.log(` => is idp authenticated => ${isAuthenticated}`);
      if (!isAuthenticated) {
        throw new Error('error-unable-to-complete-idp-signin');
      }
      console.log(` => IdP auth is good, redirect to => ${appRedirectUrl}`);
      // NOTE: this will run thru auth0-login-helper (again), recognize the SSO auth and then redirect to the account linking page
      window.location.href = appRedirectUrl;
    } catch (e) {
      logger.error(` => login with popup error => ${e.message}`);
      if (e.message.indexOf('Popup closed') < 0) {
        throw e;
      }
    }
  }

  // function override candidate, no arrow functions here...
  beforeAuthPopup(loginWithRedirectOptions, auth0Client) {
    // no default actions, override if IDP requires special actions
  }

  // function override candidate, no arrow functions here...
  afterAuthPopup() {

  }

  // Profile builders here
  // function override candidate, no arrow functions here...
  selectNewUsername(oauthUserProfile) {
    return this.selectEmail(oauthUserProfile);
  }

  async selectNewPassword(oauthUserProfile) {
    // user will never see or know this password, but extending here anyhow to cover unforeseen circumstances (like if password needs prepending with idp-id)
    // NodeJS only
    if (Auth0UtilsBrowser.isBrowser) {
      throw new Error('selectNewPassword may only be called from a secure (server) environment.');
    }

    // default behavior is just to generate a random hash
    const crypto = await import('crypto');
    const pwd = crypto.randomUUID();
    return `${Auth0Constants.USER_ONBOARDING_PASSWORD_PREFIX}${pwd}`;
  }

  // function override candidate, no arrow functions here...
  selectEmail(oauthUserProfile) {
    // Override at idp for special rules or to use bespoke IDP data
    const { email } = oauthUserProfile;
    return email;
  }

  // function override candidate, no arrow functions here...
  selectName(oauthUserProfile) {
    // Override at idp for special rules or to use bespoke IDP data
    const { name } = oauthUserProfile;
    if (name) return name;

    const { given_name, family_name } = oauthUserProfile;
    // note: other cultures use reverse order
    if (given_name && family_name) {
      return `${given_name} ${family_name}`;
    }
    if (family_name) return family_name;
    if (given_name) return given_name;

    return '';
  }

  selectGivenName(oauthUserProfile) {
    // Override at idp for special rules or to use bespoke IDP data
    const { given_name } = oauthUserProfile;
    return given_name;
  }

  selectFamilyName(oauthUserProfile) {
    // Override at idp for special rules or to use bespoke IDP data
    const { family_name } = oauthUserProfile;
    return family_name;
  }

  selectCountryCode(oauthUserProfile) {
    // Override at idp for special rules or to use bespoke IDP data
    return 'US';
  }

  selectRequestedLanguage(oauthUserProfile) {
    // Override at idp for special rules or to use bespoke IDP data
    const { locale } = oauthUserProfile;
    return locale;
  }

  selectCompanyName(oauthUserProfile) {
    // Override at idp for special rules or to use bespoke IDP data
    // No default behavior as this info is not included in a standard oauth2 profile
  }

  selectPhoneNumber(oauthUserProfile) {
    // Override at idp for special rules or to use bespoke IDP data
    // No default behavior as this info is not included in a standard oauth2 profile
  }
  // end profile builders

  // function override candidate, no arrow functions here...
  async runNewUserOnboardingActions(oauthUserProfile) {
    // No default actions; override to execute custom actions per-idp
  }

  // function override candidate, no arrow functions here...
  /**
   * @function
   * @instance
   * @memberof Auth0IdentityProvider
   * @description This function determines, using available context if this IDP is actively in the signin flow. The oauth2 flow can persist through many requests and pages and some combination of persistence (cookies, local storage) and URL (query string) will identify the current state of signin. This function uses the globally available method as set when clicking a signin with IDP button. There may be cases where signin is initiated outside of those buttons (like from the IDP itself). In that case, override this function for that IDP and implement custom detection as appropriate (e.g. shopify will redirect to app after opening an internal SSO ShipStation app, etc)
   * @param {object} eventOptions Options describing the app open event
   * @param {object} [eventOptions.req] The ExpressJS request associated with the open event.
   * @returns {boolean} A boolean indicating if this IDP is actively engaged in the oauth2 flow to complete signin
   */
  isSigninActive(eventOptions = null) {
    console.log(`isSigninActive for ${this.id} starts...`);
    const idpContextVal = Auth0UtilsBrowser.isBrowser
      ? Auth0CookieHelper.getCookieValue(Auth0Constants.AUTH0_IDP_CONTEXT_KEY)
      : eventOptions.req.cookies[Auth0Constants.AUTH0_IDP_CONTEXT_KEY]; // else, assume NodeJS ExpressJS (assumes cookies parsed to req.cookies)

    if (!idpContextVal) {
      // console.log('isSigninActive base: Could not fetch IDP from context because no context exists.');
      return false;
    }

    console.log(`isSigninActive current context => ${idpContextVal}`);
    const { idpId } = JSON.parse(idpContextVal);
    console.log(`Auth0IdentityProvider.isSigninActive() for ${this.id} => active IDP is ${idpId}`);
    return this.id === idpId;
  }

  _getAuctaneAppSigninStart = (req) => {
    // an actual client cookie set by the login page has precedence
    if (req.cookies?.AUTH0_IDP_REDIRECT_LOGIN) {
      const idpData = JSON.parse(req.cookies.AUTH0_IDP_REDIRECT_LOGIN);
      if (idpData.appLoginHandlerUrl) {
        console.log(`AUTH0_IDP_REDIRECT_LOGIN cookie found, using specified redirect => ${idpData.appLoginHandlerUrl}`);
        return idpData.appLoginHandlerUrl;
      }
    }
    // otherwise, this may be IDP initiated login, in which case, that won't be set
    // check if IDP has been configured to send an Auctane app ID, otherwise default to SSv3

    // IMPORTANT: Since it unlikely that 3rd party config will allow for any kind of dynamic behavior when performing an
    // IDP initiated signin, you may need to create multiple instances of IDP initiated redirects where each one contains
    // a URL specific to an auctane app (e.g. https://local.shipstation.com/loginauth, etc). This is ONLY necessary if
    // you need to support IDP initiated logins. For any IDP signin initiated from the tenant login page, it isn't necessary
    const qs = new URLSearchParams(req.query);
    const appLoginUrl = qs.get(Auth0Constants.APP_LOGIN_URL_KEY);
    if (appLoginUrl) {
      console.log(`APP_LOGIN_URL_KEY qs param found, using specified redirect => ${appLoginUrl}`);
      return appLoginUrl;
    }

    console.log('No app login redirect url found and no app identified by IDP, using default...');
    // Use the current TLD so this fallback works on both shipstation.com and sslocal.com (ZDE/DDE).
    const baseDomain = typeof window !== 'undefined' && window.location.hostname.endsWith('.sslocal.com')
      ? 'sslocal.com'
      : 'shipstation.com';
    return `https://local.${baseDomain}/loginauth`;
  };

  // function override candidate, no arrow functions here...
  /**
   * @function
   * @instance
   * @memberof Auth0IdentityProvider
   * @description This function is only used when initiating signin from an IDP. The normal method for initiating signin is from the tenant's login page (service provider initiated). In this case, we can easily set IDP context for the oauth2 flow immediately upon clicking the IDP signin button. There may be cases where we want to start the signin flow from an IDP (e.g. installing a third party app like shopify). In that case, this function will be used to set context and perform any other custom action required for that IDP to move into the norma signin flow. Explained another way, this function allows for custom behavior when starting auth from the IDP and then starts the the auth flow AS IF the SSO button on the login page had been clicked. This function MUST be able to set browser context in the same way that clicking a button on the login page would (since that is skipped)...
   * @param {object} eventOptions Options describing the app open event
   * @param {object} eventOptions.queryString The querystring that accompanied the callback request from the third party app provider (will vary based on provider; non-standardized)
   * @param {object} eventOptions.cookies The cookies that accompanied the callback request from the third party app provider (will vary based on provider; non-standardized)
   * @param {object} eventOptions.req The ExpressJS request associated with the open event.
   * @returns {object} responseOptions An object describing to the Auth Service, how to respond to the app open event
   * @returns {object} responseOptions.queryString The object to use a the url querystring when the Auth Service redirects back to the calling app
   * @returns {array} responseOptions.cookies An array representing cookies that should be set upon redirecting to the client. A cookie object has the following shape `{ key, value, expiryInSec }`
   */
  idpInitiatedSignin(eventOptions) {
    // Called from NodeJS (i.e. IDP calls NodeJS endpoint that calls this), default actions are to mimic SSO button click on browser by setting IDP context and IDP redirect cookies
    const appSigninRedirect = this._getAuctaneAppSigninStart(eventOptions.req);
    const queryString = {};
    queryString[Auth0Constants.AUTH0_IDP_INITIATED_KEY] = this.id;

    return {
      appSigninRedirect,
      queryString,
      cookies: [
        {
          key: Auth0Constants.AUTH0_IDP_CONTEXT_KEY,
          value: JSON.stringify({ idpId: this.id }),
          expiryInSec: Auth0Constants.GLOBAL_FLOW_TIMEOUT_SEC,
        },
        {
          key: Auth0Constants.AUTH0_IDP_REDIRECT_LOGIN,
          value: JSON.stringify({
            idpId: this.id,
            appLoginHandlerUrl: appSigninRedirect,
          }),
          expiryInSec: Auth0Constants.GLOBAL_FLOW_TIMEOUT_SEC,
        },
      ],
    };
  }

  // OAUTH2 PROXY IMPL (optional if using auth service to proxy oauth2 through a custom Auth0 social connection, otherwise will never be executed)
  // function override candidate, no arrow functions here...
  /**
   * @function
   * @instance
   * @memberof Auth0IdentityProvider
   * @description When using a custom Auth0 Social Connection, you may choose to proxy the oauth2 dance through the Auctane Auth Service (if you require additional control or dynamic functionality within oauth2). This function is called only by the Auctane Auth Service in the case that your custom social connection requires hooks or dynamic activity when coordinating the oauth2 access code grant. This function is called when the auth service is fielding the "/authorize" phase. NOTE: The custom social connection `Authorization URL` in Auth0 MUST be pointing to the Auth Service's "/oauth2proxy/authorize" endpoint and the `ss_idp_context` must be set by the client (the login page for Auth0 will automatically set this cookie on IDP button click).
   * @param {object} eventOptions Options describing the oauth2 authorize event
   * @param {object} eventOptions.queryString The querystring that accompanied the callback request from the third party app provider (will vary based on provider; non-standardized)
   * @param {object} eventOptions.cookies The cookies that accompanied the callback request from the third party app provider (will vary based on provider; non-standardized)
   * @param {object} eventOptions.req The ExpressJS request associated with the authorize event.
   * @returns {object} authorizeRedirectData An object describing how the Auth Server should conduct its redirect to the IDPs oauth2 service
   * @returns {string} authorizeRedirectData.idpAuthorizeUrl The absolute URL to where the Auth Service should redirect the authorize request (to the actual IDP, based on their docs). NOTE: This is the base URL only. Query string params should NOT be included.
   * @returns {object} authorizeRedirectData.idpAuthorizeCustomParams An object describing any custom query string params that should be used in the `/authorize` redirect ("custom" refers to any params that are NOT `response_type`, `redirect_uri`, `scope`, `state`, `client_id`; these are all standard oauth2 params and are handled automatically).
   */
  oauth2ProxyAuthorize(eventOptions) {
    // no default actions, override if IDP requires special actions
  }

  /**
   * @function
   * @instance
   * @memberof Auth0IdentityProvider
   * @description When using a custom Auth0 Social Connection, you may choose to proxy the oauth2 dance through the Auctane Auth Service (if you require additional control or dynamic functionality within oauth2). This function is called only by the Auctane Auth Service in the case that your custom social connection requires hooks or dynamic activity when coordinating the oauth2 access code grant. This function is called when the auth service is fielding the "/callback" phase. NOTE: The `ss_idp_context` must be set by the client (the login page for Auth0 will automatically set this cookie on IDP button click). The Auth service will automatically target itself as the oauth2 callback.
   * @param {object} eventOptions Options describing the oauth2 callback event
   * @param {object} eventOptions.queryString The querystring that accompanied the callback request from the third party app provider (will vary based on provider; non-standardized)
   * @param {object} eventOptions.cookies The cookies that accompanied the callback request from the third party app provider (will vary based on provider; non-standardized)
   * @param {object} eventOptions.req The ExpressJS request associated with the callback event.
   * @returns {object} An object of any shape that will be cached and returned during the oauth2 transaction's token fetch (oauth2ProxyToken)
   */
  oauth2ProxyAuthorizeCallback(eventOptions) {
    // no default actions, override if IDP requires special actions
  }

  /**
   * @function
   * @instance
   * @async
   * @memberof Auth0IdentityProvider
   * @description When using a custom Auth0 Social Connection, you may choose to proxy the oauth2 dance through the Auctane Auth Service (if you require additional control or dynamic functionality within oauth2). This function is called only by the Auctane Auth Service in the case that your custom social connection requires hooks or dynamic activity when coordinating the oauth2 access code grant. This function is called when the auth service is fielding the "/access_token" phase. NOTE: The custom social connection `Token URL` field in Auth0 MUST be pointing to the Auth Service's "/oauth2proxy/token" endpoint.
   * @param {object} eventOptions Options describing the oauth2 token event
   * @param {object} eventOptions.authorizeContext The object, if any, that was returned as part of the oauth2ProxyAuthorizeCallback (last stop at the client)
   * @param {object} eventOptions.queryString The querystring that accompanied the callback request from the third party app provider (will vary based on provider; non-standardized)
   * @param {object} eventOptions.req The ExpressJS request associated with the callback event.
   * @returns {object} tokenRedirectData An object describing how the Auth Server should conduct its redirect to the IDPs oauth2 service
   * @returns {string} tokenRedirectData.idpTokenUrl The absolute URL to where the Auth Service should redirect the authorize request (to the actual IDP, based on their docs). NOTE: This is the base URL only. Query string params should NOT be included. NOTE: this MUST be a URL to an HTTP POST endpoint.
   * @returns {object} tokenRedirectData.idpTokenCustomParams An object describing any custom query string params that should be used in the `/access_token` request (to the IDP).
   */
  oauth2ProxyToken(authorizeContext, eventOptions) {
    // no default actions, override if IDP requires special actions
  }

  /**
   * @function
   * @instance
   * @async
   * @memberof Auth0IdentityProvider
   * @description When using a custom Auth0 Social Connection, you may choose to proxy the oauth2 dance through the Auctane Auth Service (if you require additional control or dynamic functionality within oauth2). This function is called only by the Auctane Auth Service in the case that your custom social connection requires hooks or dynamic activity when coordinating the oauth2 access code grant. This function is called when the auth service is fielding has successfully authenticated a user against the custom social IDP and allows for enrichment of the profile before Auth0 makes the access and identity tokens (this custom data will be included).
   * @param {object} eventOptions Options describing the oauth2 token event
   * @param {object} eventOptions.authorizeContext The object, if any, that was returned as part of the oauth2ProxyAuthorizeCallback (last stop at the client)
   * @param {object} eventOptions.tokenResponse The newly acquired IDP token response (presumably carrying an access token, but it can have any shape). Use it to get additional data via external API or possibly include it in a user's profile data.
   * @param {object} eventOptions.queryString The querystring that accompanied the callback request from the third party app provider (will vary based on provider; non-standardized)
   * @param {object} eventOptions.cookies The cookies that accompanied the callback request from the third party app provider (will vary based on provider; non-standardized)
   * @param {object} eventOptions.req The ExpressJS request associated with the callback event.
   * @returns {object} A custom object describing user profile for the user that has just authenticated. This profile object can have any shape that is required and these properties will be passed to Auth0 for inclusion in tokens. NOTE: This data will be passed to the custom social IDP's REQUIRED `Fetch User Profile` script. Copying this script from any existing custom social IDP will likely suffice.
   */
  async oauth2ProxyUserProfile(eventOptions) {
    // no default actions, override if IDP requires special actions
  }

  reset() {
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_IDP_CONTEXT_KEY);
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_IDP_REDIRECT_LOGIN);
    Auth0UtilsBrowser.setOption({
      cookieName: Auth0Constants.AUTH0_FORCE_LOGOUT,
      value: { logoutType: Auth0Constants.LOGOUT_TYPE_REDIRECT },
      expirySec: 600,
      stringify: true,
    });
  }

  static getUserIdRegex(idpId = null, userId = null) {
    // passing in idpId = null will produce regex that matches an userId from any idp, otherwise regex will match user for specific isp.
    const idpRegex = idpId ? Auth0UtilsBrowser.escapeRegExp(idpId) : '[^|]+';
    const userIdRegex = userId ? Auth0UtilsBrowser.escapeRegExp(userId) : '\\S+';
    const builtRegex = `^(${Auth0IdentityProvider._customIdpPrefixRegex})?(${idpRegex})\\|(${userIdRegex})$`;
    console.log(`built regex => ${builtRegex}`);
    return new RegExp(builtRegex, 'g');
  }

  static getIdpIdFromUserId = (userId) => {
    console.log(`getIdpIdFromUserId for user id => ${userId}`);
    const m = Auth0IdentityProvider.getUserIdRegex().exec(userId);
    if (!m || m.length < 2) {
      throw new Error(`getIdpIdFromUserId failed! '${userId}' is not a well formed auth0 ID ([provider-id]|[user-id])`);
    }

    // The regex captures: [1]=optional oauth2| prefix, [2]=first segment (provider), [3]=rest (user_id)
    let idpId = m[2];

    // Handle enterprise OIDC/SAML providers which have format: oidc|connection|identifier or samlp|connection|identifier
    // For these, the "IdP ID" (connection name) is the SECOND segment, not the first.
    // Examples:
    //   - "google-oauth2|123456789" -> idpId = "google-oauth2" (first segment)
    //   - "oidc|woot-oauth2|plopech" -> idpId = "woot-oauth2" (second segment, i.e., connection name)
    //   - "samlp|okta|user123" -> idpId = "okta" (second segment, i.e., connection name)
    if (idpId === 'oidc' || idpId === 'samlp') {
      // m[3] contains the rest after the first pipe, e.g., "woot-oauth2|plopech"
      // Extract the connection name (everything before the next pipe)
      const restOfUserId = m[3];
      const pipeIndex = restOfUserId.indexOf('|');
      if (pipeIndex !== -1) {
        idpId = restOfUserId.substring(0, pipeIndex);
        console.log(`getIdpIdFromUserId => detected enterprise provider, extracted connection: ${idpId}`);
      }
    }

    console.log(`getIdpIdFromUserId for user id => ${userId}; found ${idpId}`);
    return idpId;
  };

  static getUserIdFromIdpUserId = (userId) => {
    const m = Auth0IdentityProvider.getUserIdRegex().exec(userId);
    if (!m || m.length < 3) {
      throw new Error(`getUserIdFromIdpUserId failed! '${userId}' is not a well formed auth0 ID ([provider-id]|[user-id])`);
    }

    return m[3];
  };

  static normalizeIdpUserIdsForAuth0User = (auth0UserObject) => {
    if (!auth0UserObject || !auth0UserObject.identities) return;

    auth0UserObject.identities.forEach((identity) => {
      identity.user_id = `${identity.provider}|${identity.user_id}`;
    });

    return auth0UserObject;
  };
}

export default Auth0IdentityProvider;
